import { Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { useActionsUtilisateur, useStructures, useUtilisateurs, type UtilisateurSaisie } from '@/api/hooks';
import { CATEGORIES_AGENT, LIBELLE_CATEGORIE, LIBELLE_PROFIL, type CategorieAgent, type Utilisateur } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OngletsAdministration } from './Onglets';

const vide = (): UtilisateurSaisie => ({ matricule: '', nom: '', prenom: '', email: '', fonction: '', structureCode: '', categorie: 'ACOL', base: 'SIEGE', province: '' });

/** Formulaire de la fiche agent (création ou modification), dans une boîte de dialogue. */
export function DialogueUtilisateur({ ouvert, onOpenChange, existant, onEnregistre }: { ouvert: boolean; onOpenChange: (o: boolean) => void; existant?: Utilisateur; onEnregistre?: (u: Utilisateur) => void }) {
  const structures = useStructures();
  const actions = useActionsUtilisateur();
  const [v, setV] = useState<UtilisateurSaisie>(existant ? { matricule: existant.matricule, nom: existant.nom, prenom: existant.prenom, email: existant.email, fonction: existant.fonction, structureCode: existant.structure?.code ?? '', categorie: existant.categorie, base: existant.base, province: existant.province ?? '' } : vide());
  const maj = (patch: Partial<UtilisateurSaisie>) => setV({ ...v, ...patch });
  const enregistrer = async () => {
    const corps: UtilisateurSaisie = { ...v, matricule: v.matricule.trim(), nom: v.nom.trim(), prenom: v.prenom.trim(), email: v.email.trim(), fonction: v.fonction.trim(), province: v.province?.trim() || undefined };
    if (!corps.matricule || !corps.nom || !corps.prenom || !corps.email || !corps.fonction || !corps.structureCode) return toast.error('Matricule, nom, prénom, adresse, fonction et structure sont obligatoires.');
    try {
      const r = existant ? await actions.modifier.mutateAsync({ id: existant.id, corps }) : await actions.creer.mutateAsync(corps);
      toast.success(existant ? 'Fiche modifiée.' : `Agent ${r.prenom} ${r.nom} créé.`);
      onOpenChange(false);
      onEnregistre?.(r);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible.');
    }
  };
  return (
    <Dialog open={ouvert} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{existant ? `Modifier la fiche de ${existant.prenom} ${existant.nom}` : 'Nouvel agent'}</DialogTitle>
          <DialogDescription>{existant ? 'Les changements sont journalisés avec l’ancienne et la nouvelle valeur.' : 'La fiche est créée sans habilitation ni compte : ajoutez-les ensuite depuis la fiche.'}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="matricule">Matricule</Label>
            <Input id="matricule" value={v.matricule} onChange={(e) => maj({ matricule: e.target.value })} placeholder="F-0031" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Adresse de courriel</Label>
            <Input id="email" type="email" value={v.email} onChange={(e) => maj({ email: e.target.value })} placeholder="prenom.nom@fonarev.cd" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="prenom">Prénom</Label>
            <Input id="prenom" value={v.prenom} onChange={(e) => maj({ prenom: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nom">Nom</Label>
            <Input id="nom" value={v.nom} onChange={(e) => maj({ nom: e.target.value })} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="fonction">Fonction</Label>
            <Input id="fonction" value={v.fonction} onChange={(e) => maj({ fonction: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="structure">Structure</Label>
            <SelectNatif id="structure" value={v.structureCode} onChange={(e) => maj({ structureCode: e.target.value })}>
              <option value="">Choisir</option>
              {(structures.data ?? []).map((s) => (
                <option key={s.id} value={s.code}>
                  {s.code} · {s.libelle}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="categorie">Catégorie (barème des missions)</Label>
            <SelectNatif id="categorie" value={v.categorie} onChange={(e) => maj({ categorie: e.target.value as CategorieAgent })}>
              {CATEGORIES_AGENT.map((c) => (
                <option key={c} value={c}>
                  {c} · {LIBELLE_CATEGORIE[c]}
                </option>
              ))}
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="base">Base</Label>
            <SelectNatif id="base" value={v.base ?? 'SIEGE'} onChange={(e) => maj({ base: e.target.value as 'SIEGE' | 'COORDINATION' })}>
              <option value="SIEGE">Siège</option>
              <option value="COORDINATION">Coordination provinciale</option>
            </SelectNatif>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="province">Province</Label>
            <Input id="province" value={v.province ?? ''} onChange={(e) => maj({ province: e.target.value })} placeholder="Kinshasa" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button onClick={enregistrer} disabled={actions.creer.isPending || actions.modifier.isPending}>
            {existant ? 'Enregistrer' : 'Créer la fiche'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function Utilisateurs() {
  const session = useSession();
  const admin = session?.utilisateur.profils.includes('ADMINISTRATEUR') ?? false;
  const navigate = useNavigate();
  const structures = useStructures();
  const [q, setQ] = useState('');
  const [structure, setStructure] = useState('');
  const [actif, setActif] = useState('true');
  const [creation, setCreation] = useState(false);
  const { data, isLoading, error } = useUtilisateurs({ q: q.trim() || undefined, structure: structure || undefined, actif: actif || undefined });

  return (
    <div>
      <PageHeader
        titre="Administration"
        sousTitre="Fiches des agents, habilitations (profil, périmètre, intérim), comptes locaux et accès Microsoft 365."
        actions={
          admin && (
            <Button onClick={() => setCreation(true)}>
              <Plus className="size-4" /> Nouvel agent
            </Button>
          )
        }
      />
      <OngletsAdministration />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Input className="w-64" placeholder="Nom, adresse, matricule, fonction" value={q} onChange={(e) => setQ(e.target.value)} />
        <SelectNatif className="w-56" value={structure} onChange={(e) => setStructure(e.target.value)} aria-label="Structure">
          <option value="">Toutes les structures</option>
          {(structures.data ?? []).map((s) => (
            <option key={s.id} value={s.code}>
              {s.code} · {s.libelle}
            </option>
          ))}
        </SelectNatif>
        <SelectNatif className="w-40" value={actif} onChange={(e) => setActif(e.target.value)} aria-label="État">
          <option value="true">Actifs</option>
          <option value="false">Désactivés</option>
          <option value="">Tous</option>
        </SelectNatif>
      </div>
      {isLoading ? (
        <Chargement />
      ) : error ? (
        <ErreurBloc erreur={error} />
      ) : !data?.length ? (
        <Vide message="Aucun agent." />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead className="hidden md:table-cell">Fonction</TableHead>
                <TableHead>Structure</TableHead>
                <TableHead>Profils</TableHead>
                <TableHead className="hidden sm:table-cell">Accès</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((u) => (
                <TableRow key={u.id} className={!u.actif ? 'opacity-60' : undefined}>
                  <TableCell>
                    <Link to={`/administration/utilisateurs/${u.id}`} className="font-medium text-primary hover:underline">
                      {u.prenom} {u.nom}
                    </Link>
                    <div className="text-xs text-muted-foreground">
                      {u.matricule} · {u.email}
                    </div>
                  </TableCell>
                  <TableCell className="hidden max-w-[28ch] truncate md:table-cell">{u.fonction}</TableCell>
                  <TableCell>{u.structure?.code ?? '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {u.profils.map((p) => (
                        <Badge key={p} variant="secondary" title={LIBELLE_PROFIL[p]}>
                          {LIBELLE_PROFIL[p]}
                        </Badge>
                      ))}
                      {!u.profils.length && <span className="text-xs text-muted-foreground">aucun</span>}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {!u.actif && <Badge variant="destructive">désactivé</Badge>}
                      {u.microsoft && <Badge variant="outline">Microsoft 365</Badge>}
                      {u.compteLocal && <Badge variant="outline">{u.compteLocal.actif ? (u.compteLocal.verrouille ? 'compte local verrouillé' : 'compte local') : 'compte local suspendu'}</Badge>}
                      {!u.microsoft && !u.compteLocal && <span className="text-xs text-muted-foreground">aucun compte</span>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {creation && <DialogueUtilisateur ouvert={creation} onOpenChange={setCreation} onEnregistre={(u) => navigate(`/administration/utilisateurs/${u.id}`)} />}
    </div>
  );
}
